import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import usePagination from "../hooks/usePagination.js";
import Modal from "../components/Shared/Modal";
import ClientForm from "../components/Client/ClientForm";
import { FORM_MODE } from "../config/constants";
import ClientInformation from "../components/ClientDetail/ClientInformation";
import BackButton from "../components/Shared/BackButton.js";
import AppointmentSection from "../components/ClientDetail/AppointmentSection.js";
import AppointmentForm from "../components/Appointment/AppointmentForm.js";
import { getClient, patchClient } from "../api/clientApi.js";
import {
  saveAppointment,
  getAppointmentsByClientId,
  patchAppointment,
} from "../api/appointmentApi.js";
import DataPagination from "../components/Shared/DataPagination.js";
import ViewAppointment from "../components/Appointment/ViewAppointment.js";
import AlertModal from "../components/Shared/AlertModal.js";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useClientContext } from "../components/Layout/Layout.js";
import ClientDetailPageSkeleton from "../components/Shared/LoadingSkeletons/ClientDetailPageSkeleton.js";
import { useToast } from "../hooks/useToast";
import useCreateIntent from "../hooks/useCreateIntent";

const ClientDetailPage = () => {
  const [showClientDrawer, setShowClientDrawer] = useState(false);
  const [showAppointmentDrawer, setShowAppointmentDrawer] = useState(false);
  const [showAppointmentView, setShowAppointmentView] = useState(false);
  const [selectedAppointment, setSelectedAppoinment] = useState(null);
  const [appointmentFormMode, setAppointmentFormMode] = useState(null);
  const [appointmentModalTitle, setAppointmentModalTitle] = useState("Create Appointment");
  const [isFormDirty, setIsFormDirty] = useState(false);
  const modalCloseRef = useRef(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const queryClient = useQueryClient();
  const { setCurrentClient } = useClientContext();
  const toast = useToast();

  const { page, limit } = usePagination(1, 5);
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const id = pathSegments[pathSegments.length - 1]; // gets '123' from '/client/123'

  const { data: client, isLoading: isLoadingClient } = useQuery({
    queryKey: ["client", id],
    queryFn: () => getClient(id),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });

  // Update context when client data is loaded
  useEffect(() => {
    if (client) {
      setCurrentClient(client);
    }
  }, [client, setCurrentClient]);

  // Clean up context when component unmounts
  useEffect(() => {
    return () => {
      setCurrentClient(null);
    };
  }, [setCurrentClient]);

  const { mutate: mutatePatchClient, mutateAsync: mutatePatchClientAsync } =
    useMutation({
      mutationFn: patchClient,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: ["client", id] });
        queryClient.invalidateQueries({ queryKey: ["clientList"] });
        queryClient.invalidateQueries({ queryKey: ["clientListTotal"] });

        // Show appropriate toast based on the operation
        if (variables.data.archived === true) {
          toast.warning("Client Archived", `${client?.fullName || 'Client'} has been archived successfully`);
        } else if (variables.data.archived === false) {
          toast.success("Client Restored", `${client?.fullName || 'Client'} is now active and can book appointments`);
        } else {
          toast.success("Client Updated", "Client information has been saved successfully");
        }

        closeDrawerHandler();
      },
      onError: (err, variables) => {
        // Archive/restore actions don't run through the form — surface a toast here.
        // Form edits handle errors locally.
        if (variables?.data?.archived !== undefined) {
          const errorMessage =
            err.response?.data?.error?.message ||
            "An error occurred while updating the client";
          toast.error("Operation Failed", errorMessage);
        }
      },
    });

  const { data: appointments, isLoading: isLoadingClientAppointments } =
    useQuery({
      queryKey: ["clientAppointments", { page, limit, clientId: id }],
      queryFn: ({ queryKey }) => {
        const [, params] = queryKey;
        return getAppointmentsByClientId(params);
      },
      placeholderData: keepPreviousData,
    });

  const { mutateAsync: mutateSaveAppointment } = useMutation({
    mutationFn: saveAppointment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["clientAppointments"] });
      toast.success("Appointment Created", "New appointment has been scheduled successfully");
      closeDrawerHandler();
    },
  });

  const { mutateAsync: mutatePatchAppointment } = useMutation({
    mutationFn: patchAppointment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientAppointments"] });
      toast.success("Appointment Updated", "Appointment details have been saved successfully");
      closeDrawerHandler();
    },
  });

  const closeDrawerHandler = () => {
    setShowClientDrawer(false);
    setShowAppointmentDrawer(false);
    setAppointmentFormMode(null);
    setIsFormDirty(false);
    setSelectedAppoinment(null);
    setShowAppointmentView(false);
  };

  const onCreateAppointmentHandler = () => {
    setShowAppointmentDrawer(true);
    setAppointmentFormMode(FORM_MODE.CREATE);
    setAppointmentModalTitle("Create Appointment");
  };

  // Mobile bottom-nav "+" deep-links here with ?new=appointment. Archived
  // clients can only manage existing appointments (matches the hidden in-page
  // Add button), so the deep-link is disabled until the client loads unarchived.
  useCreateIntent("appointment", onCreateAppointmentHandler, {
    enabled: !!client && !client.archived,
  });

  const onEditHandler = (selectedAppointment) => {
    setShowAppointmentDrawer(true);
    setAppointmentFormMode(FORM_MODE.UPDATE);
    setAppointmentModalTitle("Edit Appointment");
    if (selectedAppointment.isNextAppointment) {
      setSelectedAppoinment(appointments.nextAppointment);
      return;
    }
    const appointment = appointments.appointments.find(
      (appointment) => appointment.id === selectedAppointment.id
    );
    setSelectedAppoinment(appointment);
  };

  const archiveModal = (
    <AlertModal
      isOpen={showArchiveModal}
      modalTitle={`Archive ${client?.fullName}?`}
      modalMessage={
        <div>
          <p className="mb-3 text-sm text-ink-700">
            This client won't be able to book new appointments.
          </p>
          <Disclosure>
            <DisclosureButton className="flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700">
              <ChevronDownIcon className="h-4 w-4 transition-transform data-[open]:rotate-180" />
              What else changes
            </DisclosureButton>
            <DisclosurePanel className="mt-2">
              <ul className="list-disc list-inside space-y-1 text-sm text-ink-600">
                <li>You won't be able to create new appointments for this client</li>
                <li>All existing appointments and history are kept</li>
                <li>Client is marked "Archived" in your list</li>
                <li>Upcoming appointments stay active</li>
              </ul>
            </DisclosurePanel>
          </Disclosure>
          <p className="mt-3 text-sm text-ink-500">
            This is reversible — you can restore access anytime.
          </p>
        </div>
      }
      primaryCaption="Archive"
      primaryButtonColour="blue"
      secondaryCaption="Cancel"
      primaryClickHandler={() => {
        mutatePatchClient({ id: client?._id, data: { archived: true } });
        setShowArchiveModal(false);
      }}
      dismissModalHandler={() => {
        setShowArchiveModal(false);
      }}
      showIcon={false}
    />
  );

  const restoreClient = () => {
    mutatePatchClient({ id: client?._id, data: { archived: false } });
  };

  return (
    <div>
      {isLoadingClient || isLoadingClientAppointments ? (
        <ClientDetailPageSkeleton />
      ) : (
        <>
          {showArchiveModal ? archiveModal : null}
          {showClientDrawer ? (
            <Modal
              open={showClientDrawer}
              onClose={closeDrawerHandler}
              title="Edit Client"
              size="md"
              confirmClose={isFormDirty}
              closeRef={modalCloseRef}
            >
              <ClientForm
                closeDrawerHandler={closeDrawerHandler}
                patchClient={mutatePatchClientAsync}
                defaultClient={client}
                mode={FORM_MODE.UPDATE}
                onCancelHandler={() => modalCloseRef.current?.()}
                setIsFormDirty={setIsFormDirty}
              />
            </Modal>
          ) : null}
          {showAppointmentDrawer ? (
            <Modal
              open={showAppointmentDrawer}
              onClose={closeDrawerHandler}
              title={appointmentModalTitle}
              size="lg"
              confirmClose={isFormDirty}
              closeRef={modalCloseRef}
            >
              <AppointmentForm
                closeDrawerHandler={closeDrawerHandler}
                saveAppointment={mutateSaveAppointment}
                patchAppointment={mutatePatchAppointment}
                defaultClient={{
                  _id: client?._id,
                  fullName: client?.fullName,
                }}
                defaultClientAddress={{
                  full: client?.serviceAddress?.full,
                  placeId: client?.serviceAddress?.placeId,
                }}
                defaultAppointment={selectedAppointment}
                mode={appointmentFormMode}
                onCancelHandler={() => modalCloseRef.current?.()}
                setIsFormDirty={setIsFormDirty}
                onModalTitleChange={setAppointmentModalTitle}
              />
            </Modal>
          ) : null}
          {showAppointmentView ? (
            <Modal
              open={showAppointmentView}
              onClose={closeDrawerHandler}
              title="Appointment Details"
              size="sm"
            >
              <ViewAppointment
                appointment={selectedAppointment}
                clientName={client?.fullName}
              />
            </Modal>
          ) : null}
          <BackButton />
          <div className="mt-8">
            <ClientInformation
              client={client}
              buttons={[
                {
                  caption: "Edit",
                  onClick: () => setShowClientDrawer(true),
                  color: "blue",
                },
              ]}
              menuItems={[
                {
                  caption: client?.archived ? "Restore" : "Archive",
                  onClick: client?.archived
                    ? restoreClient
                    : () => setShowArchiveModal(true),
                },
              ]}
              onClick={() => setShowClientDrawer(true)}
            />
          </div>
          <div className="mt-8">
            <AppointmentSection
              onCreate={onCreateAppointmentHandler}
              onEdit={onEditHandler}
              appointments={
                appointments?.appointments ? appointments?.appointments : []
              }
              nextAppointment={appointments?.nextAppointment}
              onView={(appointment) => {
                setShowAppointmentView(true);
                setSelectedAppoinment(appointment);
              }}
              isClientArchived={client?.archived}
              pagination={
                <DataPagination
                  currentPage={parseInt(page)}
                  resultsPerPage={parseInt(limit)}
                  totalResults={appointments?.total ? appointments?.total : 0}
                />
              }
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ClientDetailPage;
